import {ReactElement, useEffect} from "react";
import {useBulkAddDriversMutation, useLoadSingleWorksheetLineNoteLazyQuery, VersionType} from "../../../__generated__/generated_types";
import {CopyDriverModal, CopyDriverModalOnSubmitArgs} from "./CopyDriverModal";


export interface CopyWorksheetLineItemSourceDriver {
    driverId: string;
    name: string;
    propertyId: string;
    propertyName: string;
    destinationAccountId: string;
    accountLabel: string;
    status: string;
    locked: boolean;
    valuesLocked: boolean;
    monthlyValues: (number | null)[];
}

export interface CopyWorksheetLineItemAssumptionsModalProps {
    visible: boolean;
    budgetYear: number;
    versionType: VersionType.Budget | VersionType.Reforecast;
    sourceDriver: CopyWorksheetLineItemSourceDriver;
    destinationProperties: {id: string; name: string, disabled?: boolean}[];
    defaultSelectedProperties?: string[];
    accounts: {id: string; label: string}[];
    onClose: () => void;
    onCopied: () => void;
}

export function CopyWorksheetLineItemAssumptionsModal(props: CopyWorksheetLineItemAssumptionsModalProps): ReactElement {
    const {
        visible,
        budgetYear,
        versionType,
        sourceDriver,
        destinationProperties,
        defaultSelectedProperties,
        accounts,
        onClose,
        onCopied
    } = props;

    const [bulkAddDrivers, {loading: copyLoading}] = useBulkAddDriversMutation({notifyOnNetworkStatusChange: true});
    const [fetchNote, {data: noteData, loading: noteLoading}] = useLoadSingleWorksheetLineNoteLazyQuery({fetchPolicy: "no-cache"});

    useEffect(() => {
        fetchNote({
            variables: {
                worksheetLineId: sourceDriver.driverId,
            }
        });
    }, [sourceDriver.driverId]);

    const handleCopy = async (args: CopyDriverModalOnSubmitArgs) => {
        const {
            copyNote,
            copyStatus,
            selectedPropertyIds,
            selectedAccountIds,
        } = args;

        if (noteLoading) {
            return;
        }

        let note = null;
        let noteReadOnly = false;
        if (copyNote === true && typeof noteData?.loadSingleWorksheetLineNote?.note === "string" && noteData?.loadSingleWorksheetLineNote?.note.trim() !== "") {
            note = noteData?.loadSingleWorksheetLineNote?.note;
            noteReadOnly = noteData?.loadSingleWorksheetLineNote?.isReadOnly;
        }

        const transformedValues = sourceDriver.monthlyValues.map(v => v == null ? "0" : String(v));
        const result = await bulkAddDrivers({
            variables: {
                propertyIds: selectedPropertyIds,
                accountIds: selectedAccountIds,
                year: versionType === VersionType.Reforecast ? budgetYear - 1 : budgetYear,
                versionType: versionType,
                overwriteOnDuplicate: true,
                lineItems: {
                    monthlyValues: transformedValues,
                    description: sourceDriver.name,
                    locked: sourceDriver.locked,
                    valuesLocked: sourceDriver.valuesLocked,
                    note: note,
                    noteReadOnly: noteReadOnly,
                },
                copyNote: copyNote,
                copyStatus: copyStatus,
            }
        });
        return result.data?.bulkAddDrivers;
    };

    return (
        <CopyDriverModal
            visible={visible}
            sourceDriver={sourceDriver}
            destinationProperties={destinationProperties}
            defaultSelectedProperties={defaultSelectedProperties}
            accounts={accounts}
            note={noteData?.loadSingleWorksheetLineNote?.note}
            noteLoading={noteLoading}
            copyBusy={copyLoading}
            onSubmit={handleCopy}
            onClose={onClose}
            onCopied={onCopied}
        />
    );
}
